import { Injectable } from '@angular/core';
import Highcharts, { Options, Point, YAxisOptions, YAxisPlotLinesOptions } from 'highcharts';
import _, { max } from 'lodash';
import { getBaseChart, TRAFIC } from '@shared/components/highcharts/graph/graph';
import { toSize } from '@shared/utils/size';
import { OgoTranslationService } from './ogo-translation.service';
import { SizeFormatService } from '@shared/utils/size-format.service';
import { KeyOfType } from '@shared/utils/type-utils';
import { BANDWIDTH_UNIT, REQUESTS_UNIT } from './contract.service';
import { getCssProperty } from '@shared/utils/dom-util';
import { TimedBucket } from '@shared/services/abstract-contract.service';

@Injectable({
  providedIn: 'root',
})
export class ContractChartService {
  constructor(
    private readonly translate: OgoTranslationService,
    private readonly sizeFormatService: SizeFormatService,
  ) {}

  public formaTrafficData(
    contract: Contract,
    buckets: Array<TimedBucket>,
    type: ChartType,
    overrides: Options,
  ): Options {
    let that = this;
    return _.merge(
      buildChartOptions(<Highcharts.Options>{
        title: {
          text: this.translate.instant('Contract.requests'),
        },
        yAxis: buildYAxisOptions((contract.millionRequestsPerMonth ?? 0) * REQUESTS_UNIT, buckets, 'totalRequests'),
        plotOptions: {
          [type]: {
            stacking: null,
            pointPadding: -0.2,
            groupPadding: 0.2,
          },
          series: {
            grouping: true,
          },
        },
        series: [
          {
            id: TRAFIC,
            zIndex: 2,
            color: getCssProperty('--chart-color-traffic', '#20c997'),
            type: type,
            name: this.translate.instant('Contract.invoicedRequests'),
            data: extractTimeSeries(buckets, 'invoicedRequests'),
            dataLabels: {
              enabled: true,
              inside: true,
              zIndex: 200,
              formatter: function () {
                return that.sizeFormatService.formatData(toSize(this.y));
              },
            },
          },
          {
            id: 'TOTAL_' + TRAFIC,
            zIndex: 1,
            color: getCssProperty('--chart-color-total', '#d3d3d3'),
            type: type,
            name: this.translate.instant('Total'),
            data: extractTimeSeries(buckets, 'totalRequests'),
            dataLabels: {
              enabled: false,
            },
          },
        ],
        tooltip: {
          headerFormat: '',
        },
      }),
      overrides,
    );
  }

  public formatBandwidthData(
    contract: Contract,
    buckets: Array<TimedBucket>,
    type: ChartType,
    overrides: Options,
  ): Options {
    let that = this;
    return _.merge(
      buildChartOptions(<Highcharts.Options>{
        title: {
          text: this.translate.instant('BandwidthReport.title'),
        },
        plotOptions: {
          [type]: {
            grouping: false,
            pointPadding: 0.1,
            stacking: 'normal',
          },
        },
        yAxis: buildYAxisOptions((contract.bandwidthPerMonth ?? 0) * BANDWIDTH_UNIT, buckets, 'bandwidthTotal'),
        series: [
          {
            id: 'bandwidthOut',
            zIndex: 2,
            color: getCssProperty('--chart-color-bandwidth-out', '#f7a35c'),
            type: type,
            name: this.translate.instant('Graph.seriesName.bandwidthOut'),
            data: extractTimeSeries(buckets, 'bandwidthOut'),
            dataLabels: {
              enabled: false,
            },
          },
          {
            id: 'bandwidthIn',
            zIndex: 1,
            color: getCssProperty('--chart-color-bandwidth-in', '#fed330'),
            type: type,
            name: this.translate.instant('Graph.seriesName.bandwidthIn'),
            data: extractTimeSeries(buckets, 'bandwidthIn'),
            dataLabels: {
              enabled: false,
            },
          },
          {
            id: 'bandwidthTotal',
            zIndex: 0,
            color: 'transparent',
            type: type,
            stacking: null,
            data: extractTimeSeries(buckets, 'bandwidthTotal'),
            showInLegend: false,
            dataLabels: {
              enabled: true,
              formatter: function () {
                return that.getSizeDataLabel(<Point>(<unknown>this));
              },
              style: {
                color: 'black',
              },
            },
          },
        ],
        tooltip: {
          formatter: function () {
            return that.getFormatedPoint(this, buckets);
          },
        },
      }),
      overrides,
    );
  }

  public formatNbSitesData(
    contract: Contract,
    buckets: Array<TimedBucket>,
    type: ChartType,
    overrides: Options,
  ): Options {
    return _.merge(
      buildChartOptions(<Highcharts.Options>(<unknown>{
        title: {
          text: this.translate.instant(`NbSites`),
        },
        yAxis: buildYAxisOptions((contract.nbSitesAdvanced ?? 0) + (contract.nbSitesExpert ?? 0), buckets, 'nbSites'),
        plotOptions: {
          [type]: {
            stacking: 'normal',
            pointPadding: -0.2,
            groupPadding: 0.2,
          },
          series: {
            grouping: true,
          },
        },
        series: [
          {
            id: 'nbAdvancedSites',
            type: type,
            zIndex: 3,
            color: getCssProperty('advancedMode', '#52DAF0'),
            name: this.translate.instant('AdvancedMode'),
            data: extractTimeSeries(buckets, 'nbAdvancedSites'),
            dataLabels: {
              zIndex: 200,
              enabled: true,
            },
          },
          {
            id: 'nbExpertSites',
            type: type,
            zIndex: 3,
            color: getCssProperty('--expertMode', '#42389e'),
            name: this.translate.instant('ExpertMode'),
            data: extractTimeSeries(buckets, 'nbExpertSites'),
            dataLabels: {
              zIndex: 200,
              enabled: true,
            },
          },
          {
            id: 'nbSites',
            type: type,
            zIndex: 1,
            color: getCssProperty('--chart-color-total', '#d3d3d3'),
            name: this.translate.instant('Total'),
            data: extractTimeSeries(buckets, 'nbSites'),
            stacking: null,
            dataLabels: {
              zIndex: 200,
              enabled: true,
            },
          },
        ],
      })),
      overrides,
    );
  }

  private getSizeDataLabel = (point: Point): string => {
    return this.sizeFormatService.formatSize(toSize(point.y));
  };

  private getFormatedPoint(point: Point, buckets: Array<TimedBucket>): string {
    const bucket = buckets.find((datum) => new Date(point.x).toISOString() === new Date(datum.date).toISOString());
    if (!bucket) return null;

    const anotherBandwidth =
      point.y === bucket.bandwidthIn
        ? `<p>${this.translate.translateLabel('Graph.seriesName.bandwidthOut')} ${this.sizeFormatService.formatSize(toSize(bucket.bandwidthOut))}</p>`
        : `<p>${this.translate.translateLabel('Graph.seriesName.bandwidthIn')} ${this.sizeFormatService.formatSize(toSize(bucket.bandwidthIn))}</p>`;

    const getRightOrder =
      point.y === bucket?.bandwidthIn
        ? `${anotherBandwidth}<br />${this.translate.labelify(point.series.name)}<b> ${this.sizeFormatService.formatSize(toSize(point.y))}</b>`
        : `${this.translate.labelify(point.series.name)}<b> ${this.sizeFormatService.formatSize(toSize(point.y))}</b><br />${anotherBandwidth}`;

    return `${getRightOrder}<br /><p>${this.translate.translateLabel('Graph.seriesName.totalBandwidth')} ${this.sizeFormatService.formatSize(toSize(bucket.bandwidthTotal ? bucket.bandwidthTotal : bucket.bandwidthIn + bucket.bandwidthOut))}</p>`;
  }
}

function buildChartOptions(options: Options): Options {
  const result = getBaseChart();
  _.merge(result, <Options>{
    title: {
      margin: 12,
      style: {
        fontSize: '1rem',
      },
    },
    legend: {
      margin: 12,
    },
    chart: {
      backgroundColor: null,
      spacingTop: 0,
      spacingBottom: 0,
    },
    exporting: {
      enabled: false,
    },
  });
  result.chart.zooming = {};
  result.chart.style.cursor = undefined;
  _.merge(result, options);
  return result;
}

function extractTimeSeries(buckets: TimedBucket[], field: KeyOfType<TimedBucket, number>): Array<[string, number]> {
  return buckets.map((bucket) => <[string, number]>[bucket.date, bucket[field]]).filter(([_, y]) => y != 0);
}

function buildLimitLine(value: number | undefined): YAxisPlotLinesOptions[] {
  return value
    ? [
        <YAxisPlotLinesOptions>{
          color: getCssProperty('--chart-color-limit', '#dc3545'),
          zIndex: 100,
          width: 2,
          value: value,
          dashStyle: 'LongDash',
        },
      ]
    : [];
}

function buildYAxisOptions(
  limit: number,
  buckets: TimedBucket[],
  field: KeyOfType<TimedBucket, number>,
  overrides?: YAxisOptions,
): YAxisOptions {
  const options = {
    max: max([
      _(buckets)
        .map((d) => d[field])
        .max(),
      limit,
    ]),
    plotLines: buildLimitLine(limit),
    allowDecimals: false,
    title: null,
  };
  if (overrides) _.merge(options, overrides);
  return options;
}

export type Contract = {
  number: string;
  millionRequestsPerMonth?: number;
  bandwidthPerMonth?: number;
  nbSitesAdvanced?: number;
  nbSitesExpert?: number;
};

type ChartType = 'bar' | 'column';
